import React, { useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Card,
    CardBody,
    CardHeader,
    Badge,
    Input,
    Label,
    FormGroup,
    Alert,
    Row,
    Col
} from 'reactstrap';
import { 
    approveInvoice, 
    rejectInvoice,
} from "../../slices/notifications/thunk";
import { useDispatch } from 'react-redux';
import { useCurrencyFormatter } from '../Hooks/useCurrencyFormatter';

const InvoiceReviewModal = ({ 
    isOpen, 
    toggle, 
    notification, 
    onApprove, 
    onReject, 
    onRefreshNotifications 
}) => {
    const dispatch = useDispatch();
    const [isProcessing, setIsProcessing] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectionInput, setShowRejectionInput] = useState(false);
    const [modalAlert, setModalAlert] = useState({ show: false, type: '', message: '' });
    const { formatCurrency } = useCurrencyFormatter();

    // Helper functions
    const resetModal = () => {
        setRejectionReason('');
        setShowRejectionInput(false);
        setModalAlert({ show: false, type: '', message: '' });
    };

    const handleClose = () => {
        resetModal();
        toggle();
    };

    const showAlert = (type, message) => {
        setModalAlert({ show: true, type, message });
    };

    const isAlreadyProcessed = () => {
        return notification?.is_approved !== null && notification?.is_approved !== undefined;
    };

    // Action handlers
    const handleApprove = async () => {
        if (!notification) return;
        
        if (isAlreadyProcessed()) {
            const statusText = notification.is_approved ? 'approved' : 'rejected';
            showAlert('warning', `Invoice already ${statusText}`);
            return;
        }
        
        setIsProcessing(true);
        setModalAlert({ show: false, type: '', message: '' });
        
        try {
            const result = await dispatch(approveInvoice(notification.id)).unwrap();
            
            showAlert('success', 'Invoice approved successfully and sent to SmartBill!');
            
            // Execute callbacks
            onRefreshNotifications?.();
            onApprove?.(notification.id);
            
            // Auto-close after success
            setTimeout(handleClose, 2000);
            
        } catch (error) {
            const errorMessage = error?.message || error || 'Failed to approve invoice';
            showAlert('danger', errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };
   
    const handleReject = async () => {
        
        if (isAlreadyProcessed()) {
            const statusText = notification.is_approved ? 'approved' : 'rejected';
            showAlert('warning', `Invoice already ${statusText}`);
            return;
        }
        
        setIsProcessing(true);
        setModalAlert({ show: false, type: '', message: '' });
        
        try {
            const result = await dispatch(rejectInvoice(notification.id)).unwrap();
            
            showAlert('success', 'Invoice rejected successfully');
            
            // Execute callbacks
            onRefreshNotifications?.();
            onReject?.(notification.id, rejectionReason.trim());
            
            // Auto-close after success
            setTimeout(handleClose, 2000);
            
        } catch (error) {
            const errorMessage = error?.message || error || 'Failed to reject invoice';
            showAlert('danger', errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleShowRejectionInput = () => {
        setShowRejectionInput(true);
    };

    const handleCancelRejection = () => {
        setShowRejectionInput(false);
        setRejectionReason('');
    };

    // Render helpers
    const renderClientInfo = () => (
        <Card className="mb-3">
            <CardHeader>
                <h6 className="mb-0">
                    <i className="bx bx-user me-2"></i>
                    Client Information
                </h6>
            </CardHeader>
            <CardBody>
                <Row>
                    <Col md={6}>
                        <p className="mb-2">
                            <strong>Client:</strong><br />
                            {notification.data?.smartbill_data.client.name}
                        </p>
                    </Col>
                    <Col md={6}>
                        <p className="mb-2">
                            <strong>Email:</strong><br />
                            {notification.data?.smartbill_data.client.email}
                        </p>
                    </Col>
                    <Col md={6}>
                        <p className="mb-2">
                            <strong>Address:</strong><br />
                            {notification.data?.smartbill_data.client.address}
                        </p>
                    </Col>
                    <Col md={6}>
                        <p className="mb-2">
                            <strong>Country:</strong><br />
                            {notification.data?.smartbill_data.client.country}
                        </p>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );

    const renderInvoiceDetails = () => {
        const smartbillData = notification.data?.smartbill_data;
        if (!smartbillData) return null;
        const product = smartbillData.products?.[0];

        return (
            <Card className="mb-3">
                <CardHeader>
                    <h6 className="mb-0">
                        <i className="bx bx-receipt me-2"></i>
                        Invoice Details
                    </h6>
                </CardHeader>
                <CardBody>
                    <Row>
                        <Col md={4}>
                            <p className="mb-2">
                                <strong>CompanyVAT Code:</strong><br />
                                {smartbillData.companyVatCode}
                            </p>
                             <p className="mb-2">
                                 <strong>Series:</strong><br />
                                {smartbillData.seriesName}
                            </p>
                        </Col>
                        <Col md={4}>
                            <p className="mb-2">
                                <strong>Issue Date:</strong><br />
                                {new Date(smartbillData.issueDate).toLocaleDateString('ro-RO')}
                            </p>
                             <p className="mb-2">
                                <strong>Due Date:</strong><br />
                                {new Date(smartbillData.dueDate).toLocaleDateString('ro-RO')}
                            </p>
                        </Col>
                        <Col md={4}>
                            <p className="mb-2">
                                <strong>Currency:</strong>{" "}
                                {product?.currency || 'RON'}
                            </p>
                        </Col>
                    </Row>

                    {product && (
                        <div className="mt-3">
                            <h6 className="mb-2">Product/Service:</h6>
                            <div className="bg-light p-3 rounded">
                                <p className="mb-1">
                                    <strong>{product.name}</strong>
                                </p>
                                <Row className="text-sm">
                                    <Col sm={4}>Quantity: {product.quantity}</Col>
                                    <Col sm={4}>Price: {formatCurrency(product.price, product.currency, true)}</Col>
                                    <Col sm={4}>VAT: {product.taxPercentage}%</Col>
                                </Row>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    };

    const renderActionButtons = () => {
        if (isAlreadyProcessed()) {
            return (
                <div className="text-center w-100">
                    <Badge 
                        color={notification.is_approved ? 'success' : 'danger'} 
                        className="fs-14 p-2"
                    >
                        <i className={`bx ${notification.is_approved ? 'bx-check' : 'bx-x'} me-2`}></i>
                        Invoice {notification.is_approved ? 'Approved' : 'Rejected'}
                    </Badge>
                    {(notification.data?.approved_at || notification.data?.rejected_at) && (
                        <div className="mt-2 text-muted">
                            <small>
                                {notification.is_approved ? 'Approved' : 'Rejected'} on{' '}
                                {new Date(
                                    notification.data.approved_at || notification.data.rejected_at
                                ).toLocaleString('ro-RO')}
                            </small>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <>
                <Button 
                    color="danger" 
                    outline 
                    onClick={handleReject}
                    disabled={isProcessing}
                >
                    <i className="bx bx-x me-2"></i>
                    Reject
                </Button>
                <Button 
                    color="success" 
                    onClick={handleApprove}
                    disabled={isProcessing}
                >
                    {isProcessing ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="bx bx-check me-2"></i>
                            Approve & Send
                        </>
                    )}
                </Button>
            </>
        );
    };

    if (!notification) return null;

    return (
        <Modal 
            isOpen={isOpen} 
            toggle={handleClose} 
            size="lg"
            centered
        >
            <ModalHeader toggle={handleClose}>
                <i className="bx bx-task text-primary me-2"></i>
                Invoice Review - Contract {notification?.data?.contract_id}
                {isAlreadyProcessed() && (
                    <Badge 
                        color={notification.is_approved ? 'success' : 'danger'} 
                        className="ms-2"
                    >
                        {notification.is_approved ? 'Approved' : 'Rejected'}
                    </Badge>
                )}
            </ModalHeader>
            
            <ModalBody>
                {modalAlert.show && (
                    <Alert color={modalAlert.type} className="mb-3">
                        {modalAlert.message}
                    </Alert>
                )}
                
                {renderClientInfo()}
                {/* {renderPaymentDetails()} */}
                {renderInvoiceDetails()}
                
                {showRejectionInput && !isAlreadyProcessed()}
                {isAlreadyProcessed() && !notification.is_approved }
            </ModalBody>
            
            <ModalFooter>
                {renderActionButtons()}
            </ModalFooter>
        </Modal>
    );
};

export default InvoiceReviewModal;